import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ImageGallery from "react-image-gallery";
import blankData from "../../images/blank_svg.svg";
import AxiosInstance from "../../utils/AxiosInstance";
import myformat from "dateformat";
import {
	MdEdit,
	MdFactCheck,
	MdOpenInNew,
	MdLocationOn,
	MdPlace,
	MdCheckCircle,
	MdWarning,
	MdHome,
	MdMeetingRoom,
	MdMale,
	MdFemale,
	MdStar,
	MdPerson,
	MdPhone,
	MdMyLocation,
	MdLink,
	MdSchool,
	MdCalendarToday,
	MdErrorOutline,
} from "react-icons/md";
import { ReviewSModal } from "./ReviewsModa";

const API = process.env.REACT_APP_API_URL;
const defaultImages = [{ original: `${API}/api/images/90ef/cover-image-1646410873734.jpeg` }];
// public listing path for each residence type
const publicPaths = { Hostel: "hostels", Homestel: "homestels", Obuasi_Campus: "obuasi-campus" };
const typeLabels = { Obuasi_Campus: "Obuasi Campus" };

function Field({ icon, label, value, href }) {
	const display = value || value === 0 ? value : "N/A";
	return (
		<div className="ad-field">
			<span className="ad-field-icon">{icon}</span>
			<span className="ad-field-body">
				<span className="ad-field-label">{label}</span>
				{href && value ? (
					<a className="ad-field-value" href={href} target="_blank" rel="noopener noreferrer">
						{display}
					</a>
				) : (
					<span className="ad-field-value">{display}</span>
				)}
			</span>
		</div>
	);
}

function Stars({ rating }) {
	const value = Math.round(Number(rating) || 0);
	return (
		<span className="ad-stars" aria-label={`${rating} out of 5`}>
			{[1, 2, 3, 4, 5].map((n) => (
				<MdStar key={n} className={n <= value ? "is-on" : ""} />
			))}
			<b>{rating}/5</b>
		</span>
	);
}

export default function Showhostel(props) {
	let { slug } = useParams();
	const navigate = useNavigate();
	const [Residence, setResidence] = useState();
	const [facilities, setFacilities] = useState([]);
	const [gimages, setGimages] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		const controller = new AbortController();
		const fetchData = async () => {
			setIsLoading(true);
			setHasError(false);
			try {
				const res = await AxiosInstance({
					method: "get",
					url: `${API}/api/v1/residences/${slug}`,
					signal: controller.signal,
				});
				const data = res.data?.data;
				if (!data) throw new Error("Residence not found");
				// cover image first, then the rest of the gallery
				const files = [data.coverImage, ...(data.images || [])].filter(Boolean);
				setGimages(
					files.map((file) => ({
						original: `${API}/api/images/${slug}/${file}`,
						thumbnail: `${API}/api/images/${slug}/${file}`,
					}))
				);
				setResidence(data);
				setFacilities(res.data.facilities || []);
			} catch (err) {
				if (controller.signal.aborted) return;
				console.log(err);
				setHasError(true);
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		};

		fetchData();
		return () => controller.abort();
	}, [slug]);

	if (isLoading) return <div id="dash-loader" />;

	if (hasError || !Residence) {
		return (
			<div className="ad-card ad-empty">
				<MdErrorOutline className="ad-empty-icon" />
				<h3>Couldn&apos;t load this residence</h3>
				<button className="ad-btn ad-btn--primary" onClick={() => navigate("/admin/residences")}>
					Back to residences
				</button>
			</div>
		);
	}

	const zone = Residence?.location?.zone;
	const coords = Residence?.gpsAddress?.coordinates;
	const hasCoords = Array.isArray(coords) && coords[0] !== 0 && coords[1] != null;
	const publicPath = publicPaths[Residence.residenceType];
	const reviews = Residence.reviews || [];

	const stats = [
		{ icon: <MdHome />, label: "Bed Spaces", value: Residence?.totalBedspaces },
		{ icon: <MdMeetingRoom />, label: "Rooms", value: Residence?.roomsTotal },
		{ icon: <MdMale />, label: "Male Capacity", value: Residence?.maleCapacity },
		{ icon: <MdFemale />, label: "Female Capacity", value: Residence?.femaleCapacity },
	];
	const prices = [
		{ label: "2-in-1", value: Residence?.actualPrices?.twoInOne },
		{ label: "3-in-1", value: Residence?.actualPrices?.threeInOne },
		{ label: "4-in-1", value: Residence?.actualPrices?.fourInOne },
		{ label: "Other", value: Residence?.actualPrices?.others },
	];

	return (
		<div className="ad-page">
			{/* header */}
			<section className="ad-card ad-head">
				<div className="ad-head-main">
					<div className="ad-class" title="Residence class">
						<span>{Residence?.rClass?.name ?? "N/A"}</span>
						<small>Class</small>
					</div>
					<div className="ad-head-text">
						<h2 className="ad-title">{Residence?.name}</h2>
						<div className="ad-chips">
							<span className="ad-chip">
								<MdHome /> {typeLabels[Residence.residenceType] || Residence.residenceType || "N/A"}
							</span>
							<span className="ad-chip">
								<MdLocationOn /> {Residence?.location?.name ?? "N/A"}
							</span>
							<span className="ad-chip">
								<MdPlace /> {zone?.name ?? "N/A"}
							</span>
							<span className={`ad-chip ${Residence.registered ? "ad-chip--ok" : "ad-chip--warn"}`}>
								{Residence.registered ? <MdCheckCircle /> : <MdWarning />}
								{Residence.registered ? "Registered" : "Not Registered"}
							</span>
						</div>
					</div>
				</div>
				<div className="ad-head-actions">
					<button
						className="ad-btn ad-btn--primary"
						onClick={() => navigate(`/admin/residences/${Residence?.slug}/edit`)}
					>
						<MdEdit /> Edit
					</button>
					<button
						className="ad-btn ad-btn--outline"
						onClick={() => navigate(`/admin/residences/${Residence._id}/assess`)}
					>
						<MdFactCheck /> Assess
					</button>
					{publicPath && (
						<a
							className="ad-btn ad-btn--ghost"
							href={`/${publicPath}/${Residence.slug}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							<MdOpenInNew /> Public page
						</a>
					)}
				</div>
			</section>

			<div className="ad-layout">
				<div className="ad-col">
					<section className="ad-card ad-gallery">
						<ImageGallery
							items={gimages.length ? gimages : defaultImages}
							showThumbnails={gimages.length > 1}
							showPlayButton={gimages.length > 1}
							lazyLoad
						/>
					</section>

					<section className="ad-card">
						<h3 className="ad-card-title">Statistics</h3>
						<div className="ad-stats">
							{stats.map((s) => (
								<div key={s.label} className="ad-stat">
									<span className="ad-stat-icon">{s.icon}</span>
									<span className="ad-stat-value">{s.value ?? "N/A"}</span>
									<span className="ad-stat-label">{s.label}</span>
								</div>
							))}
							<div className="ad-stat ad-stat--score">
								<span className="ad-stat-icon">
									<MdFactCheck />
								</span>
								<span className="ad-stat-value">{Residence?.score ?? "N/A"}</span>
								<span className="ad-stat-label">Assessment Score</span>
							</div>
						</div>
					</section>

					<section className="ad-card">
						<div className="ad-card-head">
							<h3 className="ad-card-title">Actual Prices (GH&#8373;)</h3>
							<span className="ad-pill">Bed spaces: {Residence?.bedSpaceStatus ?? "N/A"}</span>
						</div>
						<div className="ad-prices">
							{prices.map((p) => (
								<div key={p.label} className="ad-price">
									<span>{p.label}</span>
									<b>{p.value || "N/A"}</b>
								</div>
							))}
						</div>
					</section>

					<section className="ad-card">
						<h3 className="ad-card-title">Facilities</h3>
						{facilities.length === 0 ? (
							<p className="ad-muted">Not Available</p>
						) : (
							<ul className="ad-facilities">
								{facilities.map((item) => (
									<li key={item._id} className="ad-facility">
										<MdCheckCircle className="ad-facility-icon" />
										<span>{item?.facility?.name}</span>
										<b className="ad-facility-count">{item?.count}</b>
									</li>
								))}
							</ul>
						)}
					</section>
				</div>

				<div className="ad-col">
					<section className="ad-card">
						<h3 className="ad-card-title">Description</h3>
						<p className="ad-text">{Residence?.description || "N/A"}</p>
					</section>

					<section className="ad-card">
						<h3 className="ad-card-title">Contacts</h3>
						<div className="ad-fields">
							<Field icon={<MdPerson />} label="Owner's Name" value={Residence?.ownersName} />
							<Field icon={<MdPhone />} label="Owner's Contact" value={Residence?.ownersContact} />
							<Field icon={<MdPerson />} label="Manager's Name" value={Residence?.managersName} />
							<Field icon={<MdPhone />} label="Manager's Contact" value={Residence?.managersContact} />
							<Field icon={<MdPerson />} label="Porter's Name" value={Residence?.portersName} />
							<Field icon={<MdPhone />} label="Porter's Contact" value={Residence?.portersContact} />
						</div>
					</section>

					<section className="ad-card">
						<h3 className="ad-card-title">Location &amp; Management</h3>
						<div className="ad-fields">
							<Field icon={<MdMyLocation />} label="Digital Address" value={Residence?.digitalAddress} />
							<Field
								icon={<MdLocationOn />}
								label="GPS (Lat, Lng)"
								value={hasCoords ? `${coords[1]}, ${coords[0]}` : null}
								href={hasCoords ? `https://www.google.com/maps?q=${coords[1]},${coords[0]}` : null}
							/>
							<Field icon={<MdPlace />} label="Zone" value={zone?.name} />
							<Field icon={<MdSchool />} label="Senior Tutor" value={zone?.tutor?.name} />
							<Field
								icon={<MdLink />}
								label="Booking Link"
								value={Residence?.bookingLink}
								href={Residence?.bookingLink}
							/>
						</div>
					</section>

					<section className="ad-card">
						<div className="ad-card-head">
							<h3 className="ad-card-title">Reviews</h3>
							<span className="ad-pill">{reviews.length} total</span>
						</div>
						{reviews.length === 0 ? (
							<div className="ad-empty-reviews">
								<img src={blankData} alt="" />
								<p className="ad-muted">No reviews yet</p>
							</div>
						) : (
							<ul className="ad-reviews">
								{reviews.slice(0, 5).map((comment, i) => (
									<li className="ad-review" key={comment?._id || i}>
										<div className="ad-review-head">
											<span className="ad-avatar">
												{(comment.author?.username || "?").charAt(0).toUpperCase()}
											</span>
											<div>
												<b>{comment.author?.username || "Anonymous"}</b>
												<span className="ad-review-date">
													<MdCalendarToday />
													{comment?.createdAt
														? myformat(comment.createdAt, "mmm dS, yyyy, h:MM TT")
														: "N/A"}
												</span>
											</div>
											<Stars rating={comment.rating} />
										</div>
										<p className="ad-review-text">{comment.review}</p>
									</li>
								))}
							</ul>
						)}
						{reviews.length > 5 && <ReviewSModal reviews={reviews} name={Residence.name} />}
					</section>
				</div>
			</div>
		</div>
	);
}
